import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { Sidebar } from '@/components/layout/Sidebar'
import { Topbar } from '@/components/layout/Topbar'
import { cn } from '@/lib/cn'
import { RealtimeProvider } from '@/lib/realtime'
import { useWorkspace } from '@/lib/workspace'

/**
 * The authenticated shell.
 *
 * One column of chrome, one column of content. The sidebar is permanent from
 * the medium breakpoint up and becomes an overlay below it; the drawer closes
 * on navigation so a tap on a project does not leave it covering the page it
 * just opened.
 */
export default function AppLayout() {
  const workspace = useWorkspace()
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!drawerOpen) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  return (
    <RealtimeProvider slug={workspace.slug}>
    <div className="flex min-h-dvh">
      <aside className="hidden w-64 shrink-0 border-r border-ink-200 bg-white md:block dark:border-ink-800 dark:bg-ink-950">
        <div className="sticky top-0 h-dvh">
          <Sidebar membership={workspace.membership} />
        </div>
      </aside>

      {drawerOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-ink-950/40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div
            className={cn(
              'relative h-full w-72 max-w-[85vw] border-r border-ink-200 bg-white',
              'dark:border-ink-800 dark:bg-ink-950',
            )}
          >
            <Sidebar membership={workspace.membership} onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar membership={workspace.membership} onOpenSidebar={() => setDrawerOpen(true)} />
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
    </RealtimeProvider>
  )
}
